import { useEffect, useState } from "react";
import { Link } from "@/lib/router-compat";
import PageMeta from "@/components/PageMeta";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GrantProgramMatrix from "@/components/tools/GrantProgramMatrix";
import GrantProgramCards from "@/components/tools/GrantProgramCards";
import RepaymentTypeTable from "@/components/tools/RepaymentTypeTable";
import GrantStateSelector from "@/components/tools/GrantStateSelector";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, HeartHandshake } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import RelatedTools from "@/components/tools/RelatedTools";
import NacaReferralCard from "@/components/partners/NacaReferralCard";
import PartnerReferralCard from "@/components/partners/PartnerReferralCard";
import { chenoaFund, neighborworks } from "@/data/partners";
import { FAQS, PAGE_URL } from "@/data/lowIncomeFirstTimeBuyerGrantsSchema";


const LAST_REVIEWED = "August 2026";

const CONTENTS = [
  { id: "compare", label: "Every program at a glance" },
  { id: "federal", label: "Federal programs" },
  { id: "national", label: "Nonprofit and lender programs" },
  { id: "eligibility", label: "Who actually qualifies" },
  { id: "repayment", label: "Grant vs forgivable vs deferred vs credit" },
  { id: "state", label: "Find programs in your state" },
  { id: "faq", label: "Common questions" },
];

const ELIGIBILITY = [
  {
    term: "Income limits are set against AMI, not a national number",
    body: "Most low-income programs cap household income at 80% of area median income, with some reaching to 100% or 120% in high-cost markets. AMI is set county by county and updated annually, so the same salary can qualify in one county and not the next one over. Household size changes the limit too.",
  },
  {
    term: "\"First-time buyer\" usually means the last three years",
    body: "The standard definition is that you have not owned a primary residence during the previous three years. Prior homeowners regularly requalify. Some programs waive the rule entirely for buyers in targeted areas, and a few, like FHA and several national DPA options, never had one.",
  },
  {
    term: "HUD-approved homebuyer education is usually mandatory",
    body: "Expect a certificate from a HUD-approved counseling agency or an approved online course. Take it early — closings get delayed when the certificate is the last missing document, and some programs require the course to be completed before you go under contract.",
  },
  {
    term: "Credit score minimums are program-set, not universal",
    body: "Many assistance programs sit on top of an FHA first mortgage and follow that loan's underwriting, commonly looking for a mid-600s score, though some accept lower with compensating factors and others require more. The assistance program can set a higher floor than the mortgage itself.",
  },
  {
    term: "You have to live there",
    body: "Owner-occupancy is near-universal. Assistance is not available for investment properties or second homes, and moving out during the affordability period is one of the most common triggers for having to repay a forgivable second.",
  },
  {
    term: "Rural programs are checked by address",
    body: "USDA eligibility is drawn on a map, not by how a town feels. Plenty of outer-suburban addresses qualify. Always check the exact property address rather than the city name.",
  },
  {
    term: "Voucher holders have a separate path",
    body: "If you hold a Housing Choice Voucher, your housing authority may let you apply it toward a mortgage instead of rent — but only if that PHA has adopted the homeownership option, and minimum income and employment rules typically apply.",
  },
];



const LowIncomeFirstTimeBuyerGrants = () => {
  const [openFaq, setOpenFaq] = useState<string>("");

  useEffect(() => {
    trackEvent("grant_guide_view", { entry_section: "page_load" });
  }, []);

  const handleFaqChange = (value: string) => {
    setOpenFaq(value);
    if (value) {
      trackEvent("faq_open", { entry_section: "faq", option_type: value });
    }
  };

  const matchingToolStart = (section: string) =>
    trackEvent("matching_tool_start", {
      entry_section: section,
      assistance_type: "grant",
      repayment_type: "mixed",
    });



  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageMeta
        title="Grants for Low-Income First-Time Home Buyers (2026)"
        description="Yes, grants exist — but many are forgivable or deferred loans. Compare FHLBank set-asides, HUD vouchers, USDA 502 Direct, HOME funds, NHF, Chenoa, NACA, Habitat, and bank grants, then find programs in your state."
        path="/tools/low-income-first-time-buyer-grants"
      />
      <Header />

      <main className="flex-1">
        <div className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl">
          <Card className="mb-6 border-primary/30 bg-primary/5">
            <CardContent className="p-5 sm:p-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-primary">Short answer</p>
              <p className="text-base leading-relaxed text-foreground sm:text-lg">
                Yes &mdash; there is real money for low-income first-time buyers, but{" "}
                <strong>most of what is called a "grant" is actually a forgivable or deferred second loan</strong>.
                True grants come mainly from FHLBank set-asides and large bank programs. Forgivable and deferred help
                comes from HOME-funded city and county programs and national DPA sponsors. Nearly all of it caps
                income around 80% of area median income, requires homebuyer education, and requires you to live in
                the home.
              </p>
            </CardContent>
          </Card>

          <h1 className="mb-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Grants for Low-Income First-Time Home Buyers
          </h1>
          <p className="mb-3 text-base text-muted-foreground sm:text-lg">
            This guide names the programs that actually exist, labels each one honestly as a grant, a forgivable loan,
            a deferred loan, or a lender credit, and lays out the eligibility rules that decide who gets them &mdash;
            income limits, the first-time definition, education requirements, and occupancy rules.
          </p>
          <p className="mb-6 text-xs text-muted-foreground">
            Last reviewed: {LAST_REVIEWED}. Program terms, income limits, and funding availability change frequently
            and vary by state, county, and lender. Treat everything here as a starting point to verify, not a
            guarantee of eligibility or funding.
          </p>

          <Card className="mb-8 border-primary/30 bg-primary/5 print:hidden">
            <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:p-6">
              <div>
                <p className="mb-1 font-bold text-foreground">See what you may qualify for</p>
                <p className="text-sm text-muted-foreground">
                  Run your ZIP through the matching tool to surface federal, state, county, and nonprofit programs
                  together.
                </p>
              </div>
              <Button asChild className="shrink-0 sm:ml-auto" onClick={() => matchingToolStart("hero")}>
                <Link to="/grant-finder?assistance_type=grant&income_band=low&buyer_status=first_time">
                  Find my programs
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <nav
            aria-label="On this page"
            className="sticky top-16 z-20 mb-10 rounded-xl border border-border bg-card/95 p-5 shadow-sm backdrop-blur print:hidden"
          >
            <p className="mb-3 text-sm font-bold text-foreground">On this page</p>
            <ul className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {CONTENTS.map((c) => (
                <li key={c.id}>
                  <a href={`#${c.id}`} className="text-primary hover:underline">
                    {c.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <section id="compare" className="mb-12 scroll-mt-24">
            <h2 className="mb-2 text-2xl font-bold text-foreground">Every program at a glance</h2>
            <p className="mb-5 text-sm text-muted-foreground">
              Provider, geography, income rule, first-time rule, education requirement, and what kind of money it
              really is. Dollar amounts are deliberately left out because they change by market and funding round
              &mdash; the matching tool carries the live numbers.
            </p>
            <GrantProgramMatrix />
          </section>

          <section id="federal" className="mb-12 scroll-mt-24">
            <h2 className="mb-2 text-2xl font-bold text-foreground">Federal programs</h2>
            <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
              Federal money rarely reaches buyers directly. It flows through member banks, housing authorities, and
              local governments, which is why the same federal program looks different depending on where you live.
              Tap a program to see its full rule set.
            </p>
            <GrantProgramCards group="federal" entrySection="federal" />
          </section>

          <section id="national" className="mb-12 scroll-mt-24">
            <h2 className="mb-2 text-2xl font-bold text-foreground">National, nonprofit, and lender programs</h2>
            <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
              These are the names buyers hear most often. Some are true grants, some are forgivable seconds, and a
              couple are not assistance at all &mdash; they are mortgages built to need less cash. Each card states
              which.
            </p>
            <GrantProgramCards group="national" entrySection="national" />
          </section>

          <section id="eligibility" className="mb-12 scroll-mt-24">
            <h2 className="mb-3 text-2xl font-bold text-foreground">Who actually qualifies</h2>
            <Card>
              <CardContent className="p-5">
                <dl className="space-y-4">
                  {ELIGIBILITY.map((e) => (
                    <div key={e.term}>
                      <dt className="text-sm font-bold text-foreground">{e.term}</dt>
                      <dd className="text-sm leading-relaxed text-muted-foreground">{e.body}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
          </section>

          <section id="repayment" className="mb-12 scroll-mt-24">
            <h2 className="mb-2 text-2xl font-bold text-foreground">
              True grant vs forgivable loan vs deferred loan vs lender credit
            </h2>
            <p className="mb-5 text-sm text-muted-foreground">
              This is where most confusion lives. Four things get called a grant, and only one of them always is.
            </p>
            <RepaymentTypeTable />
          </section>

          <section id="state" className="mb-12 scroll-mt-24">
            <h2 className="mb-2 text-2xl font-bold text-foreground">Find programs in your state</h2>
            <p className="mb-5 text-sm text-muted-foreground">
              State housing finance agencies run the largest single pool of first-time buyer assistance, layered on
              top of city and county programs.
            </p>
            <GrantStateSelector />
          </section>

          <Card className="mb-10 border-l-4 border-l-primary">
            <CardContent className="p-5 sm:p-6">
              <div className="mb-2 flex items-center gap-2">
                <HeartHandshake className="h-5 w-5 shrink-0 text-primary" aria-hidden />
                <h2 className="text-lg font-bold text-foreground">If you think you probably don't qualify</h2>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Most people who assume they are ineligible have never actually checked. Income limits are local and
                move every year, prior homeownership usually stops counting after three years, and credit
                requirements are lower than the internet suggests. Checking costs nothing and does not affect your
                credit. Even if nothing matches today, a HUD-approved counselor will map out exactly what has to
                change &mdash; for free.
              </p>
            </CardContent>
          </Card>

          <NacaReferralCard surface="low_income_grants" className="mb-10" />
          <PartnerReferralCard partner={neighborworks} surface="low_income_grants" className="mb-10" />
          <PartnerReferralCard partner={chenoaFund} surface="low_income_grants" className="mb-10" />



          <section id="faq" className="mb-12 scroll-mt-24">
            <h2 className="mb-4 text-2xl font-bold text-foreground">Common questions</h2>
            <Accordion
              type="single"
              collapsible
              value={openFaq}
              onValueChange={handleFaqChange}
              className="rounded-xl border border-border bg-card px-4 [&_[role=region][data-state=closed]]:hidden"
            >
              {FAQS.map((faq) => (
                <AccordionItem key={faq.id} value={faq.id}>
                  <AccordionTrigger className="text-left text-base font-semibold">{faq.q}</AccordionTrigger>
                  <AccordionContent
                    forceMount
                    className="text-sm leading-relaxed text-muted-foreground"
                  >
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <Card className="mb-10 border-primary/30 bg-primary/5 print:hidden">
            <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:p-6">
              <div>
                <p className="mb-1 font-bold text-foreground">Check which programs you match today</p>
                <p className="text-sm text-muted-foreground">
                  Free, no credit impact, and it covers federal, state, county, and nonprofit sources at once.
                </p>
              </div>
              <Button asChild className="shrink-0 sm:ml-auto" onClick={() => matchingToolStart("final_cta")}>
                <Link to="/grant-finder?assistance_type=grant&income_band=low&buyer_status=first_time">
                  Find my programs
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <section className="mb-6 print:hidden">
            <h2 className="mb-3 text-lg font-bold text-foreground">Keep going</h2>
            <div className="flex flex-wrap gap-3 text-sm">
              <Link to="/tools/down-payment-assistance-estimate" className="text-primary hover:underline">
                Estimate how much you could get
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/repayment-triggers" className="text-primary hover:underline">
                When assistance has to be paid back
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/combining-assistance-programs" className="text-primary hover:underline">
                Can you combine assistance programs?
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/high-income-down-payment-help" className="text-primary hover:underline">
                Options if you earn too much
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/raise-credit-score-before-mortgage" className="text-primary hover:underline">
                Raise your score before applying
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/down-payment-planner" className="text-primary hover:underline">
                Plan your down payment
              </Link>
            </div>
          </section>


          <p className="text-xs text-muted-foreground">
            Educational information only &mdash; not legal, financial, or tax advice, and not an offer of credit or a
            commitment to lend. Program names are referenced for illustration; Home Approach does not administer
            them. Income limits, forgiveness terms, funding availability, and lender participation change over time
            and differ by location. Verify every requirement with the program sponsor and your own lender before
            acting.
          </p>
        </div>
      </main>

      <RelatedTools currentPath="/tools/low-income-first-time-buyer-grants" />
      <Footer />
    </div>
  );
};

export default LowIncomeFirstTimeBuyerGrants;